/**
 * O bloco "Ficha do paciente" no cadastro do cliente (#811).
 *
 * ── Só com a clínica ligada ──────────────────────────────────────────────
 *
 * Quem decide se o bloco existe é quem chama: ele só é montado com a
 * funcionalidade "clinica" ligada na organização. Fora dela a rota responde
 * 404, e um bloco vazio numa loja de carros seria ruído.
 *
 * ── Editar no lugar, como o cadastro ─────────────────────────────────────
 *
 * Pela mesma razão do "Editar cadastro": um diálogo tamparia a tela enquanto
 * a pessoa confere o que o paciente disse na conversa. O botão só aparece
 * para quem edita cliente; a API recusa os outros de qualquer forma.
 *
 * ── Dois blocos, uma ficha (16/10/2026) ──────────────────────────────────
 *
 * A aba "Cadastro" mostra a ficha em dois blocos: "Dados do paciente" (pessoa
 * e endereço) e "Contato e emergência". Cada um edita só os próprios campos,
 * mas a API recebe a ficha inteira: o formulário nasce da ficha lida, e os
 * campos do outro bloco vão como estavam. A consulta é a mesma, no cache.
 *
 * ── Quando outra pessoa salvou antes ─────────────────────────────────────
 *
 * A ficha vai inteira, com a versão lida. Se alguém salvou no meio, a API
 * recusa, e a tela diz isso com todas as letras e oferece recarregar. Gravar
 * por cima apagaria, sem aviso, o que a outra pessoa acabou de escrever.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErroDaApi, type FichaDoPaciente } from '@cliqcrm/contracts';
import { useApi, useFichaDoPaciente } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, CampoSelecao, Ficha, type LinhaFicha } from '@/shared/ui';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import { Bloco } from './bloco';
import {
  corpoDe,
  dataPorExtenso,
  formularioDe,
  hojeLocal,
  idadeEm,
  lugarDe,
  mascararCep,
  NOMES_DE_CANAL,
  NOMES_DE_ESTADO_CIVIL,
  NOMES_DE_HORARIO,
  preferenciaDe,
  ruaDe,
  UFS,
  type FormularioDaFicha,
} from '../lib/ficha-do-paciente';
import './bloco-da-ficha.css';

/** Qual metade da ficha o bloco mostra. */
export type ParteDaFicha = 'pessoa' | 'contato';

const TITULO: Readonly<Record<ParteDaFicha, string>> = {
  pessoa: 'Dados do paciente',
  contato: 'Contato e emergência',
};

export function FichaDoPacienteNoCliente({
  clienteId,
  podeEditar,
  parte,
}: {
  clienteId: string;
  podeEditar: boolean;
  parte: ParteDaFicha;
}): ReactNode {
  const consulta = useFichaDoPaciente(clienteId, true);
  const [editando, definirEditando] = useState(false);
  const titulo = TITULO[parte];

  if (consulta.isPending) {
    return (
      <Bloco titulo={titulo}>
        <Carregando forma="ficha" linhas={3} rotulo="Carregando a ficha" />
      </Bloco>
    );
  }
  if (consulta.isError) {
    return (
      <Bloco titulo={titulo}>
        <Erro
          erro={consulta.error}
          titulo="Não foi possível abrir a ficha"
          aoRepetir={() => void consulta.refetch()}
        />
      </Bloco>
    );
  }

  const ficha = consulta.data;
  if (editando) {
    return (
      <Bloco titulo={titulo} largo>
        <FichaEmEdicao
          clienteId={clienteId}
          ficha={ficha}
          parte={parte}
          aoFechar={() => definirEditando(false)}
        />
      </Bloco>
    );
  }

  const linhas = parte === 'pessoa' ? linhasDaPessoa(ficha) : linhasDoContato(ficha);
  const emBranco = linhas.every((l) => l.vazio === true);
  return (
    <Bloco
      titulo={titulo}
      acao={
        podeEditar && (
          <Botao
            miudo
            variante="fantasma"
            aria-label={`${emBranco ? 'Preencher' : 'Editar'} ${titulo.toLowerCase()}`}
            onClick={() => definirEditando(true)}
          >
            {emBranco ? 'Preencher' : 'Editar'}
          </Botao>
        )
      }
    >
      {emBranco ? (
        <p className="ficha-paciente__nota">
          {parte === 'pessoa'
            ? 'A ficha deste paciente ainda não foi preenchida.'
            : 'Nenhuma preferência de contato nem contato de emergência informados.'}
        </p>
      ) : (
        <Ficha larguraRotulo="110px" linhas={linhas} />
      )}
    </Bloco>
  );
}

function linha(rotulo: string, valor: ReactNode | null): LinhaFicha {
  return valor === null ? { rotulo, valor: 'não informado', vazio: true } : { rotulo, valor };
}

function linhasDaPessoa(ficha: FichaDoPaciente): LinhaFicha[] {
  const rua = ruaDe(ficha.endereco);
  const lugar = lugarDe(ficha.endereco);

  return [
    linha('Nome social', ficha.nome_social),
    linha(
      'Nascimento',
      ficha.nascimento ? (
        <>
          {dataPorExtenso(ficha.nascimento)}
          <span className="ficha__apoio">
            {', '}
            {idadeEm(ficha.nascimento, hojeLocal())} anos
          </span>
        </>
      ) : null,
    ),
    linha('Profissão', ficha.profissao),
    linha('Estado civil', ficha.estado_civil ? NOMES_DE_ESTADO_CIVIL[ficha.estado_civil] : null),
    linha(
      'Endereço',
      rua || lugar ? (
        <>
          {rua ?? lugar}
          {rua && lugar && <span className="ficha-paciente__segunda-linha">{lugar}</span>}
        </>
      ) : null,
    ),
  ];
}

function linhasDoContato(ficha: FichaDoPaciente): LinhaFicha[] {
  return [
    linha('Prefere', preferenciaDe(ficha)),
    linha('Emergência', ficha.emergencia.nome),
    linha('Telefone dele', ficha.emergencia.telefone),
  ];
}

function FichaEmEdicao({
  clienteId,
  ficha,
  parte,
  aoFechar,
}: {
  clienteId: string;
  ficha: FichaDoPaciente;
  parte: ParteDaFicha;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [formulario, definirFormulario] = useState<FormularioDaFicha>(() => formularioDe(ficha));
  const hoje = hojeLocal();

  const mudar =
    (campo: keyof FormularioDaFicha) =>
    (evento: { currentTarget: { value: string } }): void => {
      const valor = evento.currentTarget.value;
      definirFormulario((atual) => ({
        ...atual,
        [campo]: campo === 'cep' ? mascararCep(valor) : valor,
      }));
    };

  const salvar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.salvarFichaDoPaciente({
        caminho: { id: clienteId },
        corpo: corpoDe(formulario, ficha.versao),
      });
    },
    onSuccess: (salva) => {
      cache.setQueryData(chaves.fichaDoPaciente(clienteId), salva);
      aoFechar();
    },
  });

  const conflito =
    salvar.error instanceof ErroDaApi && salvar.error.codigo === 'CONFLITO_DE_VERSAO';

  return (
    <form
      className="ficha-paciente__formulario"
      onSubmit={(evento) => {
        evento.preventDefault();
        salvar.mutate();
      }}
    >
      {parte === 'pessoa' && (
        <>
          <fieldset className="ficha-paciente__grupo">
            <legend>Pessoa</legend>
            <Campo
              id="ficha-nome-social"
              rotulo="Nome social"
              type="text"
              value={formulario.nomeSocial}
              maxLength={120}
              ajuda="Como o paciente prefere ser chamado, se for diferente do nome."
              onChange={mudar('nomeSocial')}
            />
            <Campo
              id="ficha-nascimento"
              rotulo="Nascimento"
              type="date"
              value={formulario.nascimento}
              min="1900-01-01"
              max={hoje}
              onChange={mudar('nascimento')}
            />
            <Campo
              id="ficha-profissao"
              rotulo="Profissão"
              type="text"
              value={formulario.profissao}
              maxLength={100}
              onChange={mudar('profissao')}
            />
            <CampoSelecao
              id="ficha-estado-civil"
              rotulo="Estado civil"
              value={formulario.estadoCivil}
              onChange={mudar('estadoCivil')}
            >
              <option value="">Não informado</option>
              {Object.entries(NOMES_DE_ESTADO_CIVIL).map(([chave, nome]) => (
                <option key={chave} value={chave}>
                  {nome}
                </option>
              ))}
            </CampoSelecao>
          </fieldset>

          <fieldset className="ficha-paciente__grupo">
            <legend>Endereço</legend>
            <Campo
              id="ficha-cep"
              rotulo="CEP"
              type="text"
              inputMode="numeric"
              value={formulario.cep}
              maxLength={9}
              onChange={mudar('cep')}
            />
            <Campo
              id="ficha-logradouro"
              rotulo="Rua"
              type="text"
              value={formulario.logradouro}
              maxLength={200}
              onChange={mudar('logradouro')}
            />
            <Campo
              id="ficha-numero"
              rotulo="Número"
              type="text"
              value={formulario.numero}
              maxLength={20}
              onChange={mudar('numero')}
            />
            <Campo
              id="ficha-complemento"
              rotulo="Complemento"
              type="text"
              value={formulario.complemento}
              maxLength={100}
              onChange={mudar('complemento')}
            />
            <Campo
              id="ficha-bairro"
              rotulo="Bairro"
              type="text"
              value={formulario.bairro}
              maxLength={100}
              onChange={mudar('bairro')}
            />
            <Campo
              id="ficha-cidade"
              rotulo="Cidade"
              type="text"
              value={formulario.cidade}
              maxLength={100}
              onChange={mudar('cidade')}
            />
            <CampoSelecao id="ficha-uf" rotulo="UF" value={formulario.uf} onChange={mudar('uf')}>
              <option value="">Não informada</option>
              {UFS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </CampoSelecao>
          </fieldset>
        </>
      )}

      {parte === 'contato' && (
        <fieldset className="ficha-paciente__grupo">
          <legend>Contato</legend>
          <Campo
            id="ficha-emergencia-nome"
            rotulo="Contato de emergência"
            type="text"
            value={formulario.emergenciaNome}
            maxLength={120}
            onChange={mudar('emergenciaNome')}
          />
          <Campo
            id="ficha-emergencia-telefone"
            rotulo="Telefone de emergência"
            type="tel"
            value={formulario.emergenciaTelefone}
            maxLength={40}
            ajuda="Com DDD."
            onChange={mudar('emergenciaTelefone')}
          />
          <CampoSelecao
            id="ficha-canal"
            rotulo="Prefere ser contatado por"
            value={formulario.canalPreferido}
            onChange={mudar('canalPreferido')}
          >
            <option value="">Sem preferência</option>
            {Object.entries(NOMES_DE_CANAL).map(([chave, nome]) => (
              <option key={chave} value={chave}>
                {nome}
              </option>
            ))}
          </CampoSelecao>
          <CampoSelecao
            id="ficha-horario"
            rotulo="Melhor horário"
            value={formulario.horarioPreferido}
            onChange={mudar('horarioPreferido')}
          >
            <option value="">Sem preferência</option>
            {Object.entries(NOMES_DE_HORARIO).map(([chave, nome]) => (
              <option key={chave} value={chave}>
                {nome}
              </option>
            ))}
          </CampoSelecao>
        </fieldset>
      )}

      {salvar.isError && (
        <p className="ficha-paciente__erro" role="alert">
          {conflito
            ? 'Outra pessoa salvou esta ficha enquanto você editava. Recarregue para ver a versão atual e refaça a sua alteração.'
            : recadoDeErro(salvar.error, { generica: 'Não foi possível salvar a ficha.' })}
        </p>
      )}

      <div className="ficha-paciente__acoes">
        {conflito ? (
          <Botao
            variante="primario"
            type="button"
            onClick={() => {
              void cache.invalidateQueries({ queryKey: chaves.fichaDoPaciente(clienteId) });
              aoFechar();
            }}
          >
            Recarregar a ficha
          </Botao>
        ) : (
          <Botao
            variante="primario"
            type="submit"
            desabilitado={salvar.isPending}
            motivo="Salvando…"
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Botao>
        )}
        <Botao variante="fantasma" type="button" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}
